/**
 * Helpers partagés pour les générateurs d'exercices TanoEd.
 *
 * Deux catégories :
 * 1. Fonctions utilitaires (aléatoire, formatage, manipulation)
 * 2. Helpers de payload (construisent le format BanqueQuestion standard)
 */

// ─────────────────────────────────────────────────
// 1. FONCTIONS UTILITAIRES
// ─────────────────────────────────────────────────

/**
 * Mélange un tableau (Fisher-Yates). Ne modifie pas l'original.
 * @param {Array} arr
 * @returns {Array} Copie mélangée
 */
export const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

/**
 * TIRAGE SANS REMISE pour les gens à PETIT domaine (demande prof
 * 2026-08-21, gen_1028) : on retire jusqu'à obtenir une signature JAMAIS
 * servie depuis le début du cycle — l'élève voit donc toutes les questions
 * uniques avant qu'une seule ne revienne. Quand le sac est épuisé (plus
 * rien de neuf en `essais` tentatives), il est rebattu, en évitant si
 * possible de rouvrir sur la question qui vient d'être posée.
 * L'état vit en module (le temps de l'onglet — rechargement = oubli), rien
 * n'est persisté, aucun composant n'est touché ; taille plafonnée en filet.
 *
 * @param {string} cle - Identifiant du flux (ex. 'gen_1028:qcm').
 * @param {Function} tirer - () => tirage
 * @param {Function} signatureDe - (tirage) => string comparable (la QUESTION,
 *   pas l'habillage : les grilles de distracteurs restent libres)
 * @param {number} [essais] - Tentatives maximum (défaut 80)
 * @returns {*} Un tirage inédit dans le cycle courant si possible
 */
const SACS_SANS_REMISE = new Map();
export const tirageSansRemise = (cle, tirer, signatureDe, essais = 80) => {
    let sac = SACS_SANS_REMISE.get(cle);
    if (!sac || sac.vus.size > 500) {
        sac = { vus: new Set(), derniere: null };
        SACS_SANS_REMISE.set(cle, sac);
    }
    let tirage = tirer();
    let sig = signatureDe(tirage);
    for (let k = 0; k < essais && sac.vus.has(sig); k++) {
        tirage = tirer();
        sig = signatureDe(tirage);
    }
    if (sac.vus.has(sig)) {
        // sac épuisé : on rebat, en évitant la question qui vient de sortir
        sac.vus.clear();
        for (let k = 0; k < 8 && sig === sac.derniere; k++) {
            tirage = tirer();
            sig = signatureDe(tirage);
        }
    }
    sac.vus.add(sig);
    sac.derniere = sig;
    return tirage;
};

/**
 * Entier aléatoire entre min et max inclus.
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export const randInt = (min, max) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

/**
 * Élément au hasard dans un tableau.
 * @param {Array} arr
 * @returns {*}
 */
export const randomChoice = (arr) =>
    arr[Math.floor(Math.random() * arr.length)];

/**
 * N éléments distincts au hasard dans un tableau.
 * @param {Array} arr
 * @param {number} n
 * @returns {Array}
 */
export const randomChoiceMultiple = (arr, n) =>
    shuffle(arr).slice(0, n);

/**
 * Séquences interdites dans les noms tirés au hasard (tags d'unicité
 * « Tableau XKZQ », noms de figures « quadrilatère MNPQ »...). Un tirage de
 * lettres SANS filtre finit statistiquement par écrire un gros mot dans un
 * énoncé (vu en classe : « Flèche TFUCK »). Liste volontairement large :
 * mots crus FR/EN sans lettre répétée (un tirage sans remise ne peut pas
 * produire de répétition, inutile de lister CACA/MERDE) + acronymes chargés
 * (PD, NTM...). Vérifiée comme SOUS-CHAÎNE de la séquence jointe.
 */
const SEQUENCES_INTERDITES = [
    'FUCK', 'SHIT', 'CUNT', 'DICK', 'PUTE', 'PUTA', 'BITE', 'CUL', 'ZOB',
    'ANUS', 'PENIS', 'VAGIN', 'SEIN', 'SUCE', 'NIQUE', 'BAISE', 'SALOP',
    'VIOL', 'PORN', 'SEX', 'GAY', 'NAZI', 'JUIF', 'FICK', 'KURWA', 'MERD',
    'CHIE', 'CON', 'PET', 'PROUT', 'HEIL', 'PD', 'WC', 'NTM', 'TG', 'DTC',
    'FDP', 'WTF', 'BDSM', 'LSD', 'THC',
];

/** La chaîne contient-elle une séquence interdite (insensible à la casse) ? */
export const contientSequenceInterdite = (s) => {
    const up = String(s).toUpperCase();
    return SEQUENCES_INTERDITES.some((m) => up.includes(m));
};

/**
 * Un ensemble de lettres peut-il RECOMPOSER un mot interdit ? Test
 * d'inclusion (exact : les mots de la liste n'ont pas de lettre répétée).
 * Nécessaire car les générateurs recombinent les lettres d'un tirage dans
 * tous les ordres (segment [AB] et distance BA, angle L1-O-L2...) — filtrer
 * la seule séquence jointe laissait passer « PD », « TG », « CON »...
 */
export const lettresRisquees = (lettres) => {
    const set = new Set(lettres.map((c) => String(c).toUpperCase()));
    return SEQUENCES_INTERDITES.some((mot) => [...mot].every((c) => set.has(c)));
};

/**
 * Tire n lettres distinctes dans `pool` en garantissant qu'AUCUN mot
 * interdit ne peut être recomposé avec (quel que soit l'ordre d'affichage).
 * Remplaçant direct des `pickLetters`/`pickN` locaux des générateurs (même
 * retour : tableau de lettres). Un tirage propre coûte exactement un shuffle
 * (identique à l'ancien comportement) ; re-tirage borné sinon, avec repli
 * consonnes sans paire interdite, jamais atteint en pratique.
 */
export const pickLettresSains = (pool, n) => {
    for (let i = 0; i < 40; i++) {
        const tirage = shuffle(pool).slice(0, n);
        if (!lettresRisquees(tirage)) return tirage;
    }
    return 'BCFHJKLMNQRVXZ'.split('').slice(0, n);
};

/**
 * Chiffre à un rang donné dans un nombre.
 * rang 0 = unités, 1 = dizaines, 2 = centaines, etc.
 * @param {number} nombre
 * @param {number} rang
 * @returns {number} Le chiffre (0-9)
 */
export const digitAtRang = (nombre, rang) =>
    Math.floor(Math.abs(nombre) / Math.pow(10, rang)) % 10;

/**
 * Formate un nombre avec des espaces comme séparateurs de milliers.
 * Ex: 1234567 → "1 234 567"
 * @param {number} n
 * @returns {string}
 */
export const formatNumber = (n) =>
    n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

/**
 * Un nombre qui N'EST PAS une quantité : année, code, numéro. L'affichage
 * espace les milliers de tout entier de 4 chiffres et plus (utils/milliers.js,
 * règle globale du 2026-09-06) ; ce marqueur, posé par l'auteur qui seul sait
 * ce qu'est le nombre, le laisse tel quel. Vaut dans un énoncé (HTML) ; un
 * choix de QCM est du texte simple et n'accepte pas de balise.
 *   `En ${brut(2024)}, …` → « En 2024 », jamais « En 2 024 ».
 * @param {number|string} n
 * @returns {string}
 */
export const brut = (n) => `<span class="nb-brut">${n}</span>`;

/**
 * Génère un nombre entier aléatoire avec un nombre de chiffres donné.
 * Ex: randomNumberWithDigits(4) → entre 1000 et 9999
 * @param {number} nbDigits
 * @returns {number}
 */
export const randomNumberWithDigits = (nbDigits) =>
    randInt(Math.pow(10, nbDigits - 1), Math.pow(10, nbDigits) - 1);

/**
 * Génère N distracteurs uniques différents de la bonne réponse.
 * Utilise une fonction génératrice pour proposer des valeurs.
 * @param {*} correct - La bonne réponse (à exclure)
 * @param {number} n - Nombre de distracteurs voulus
 * @param {Function} generateFn - Fonction qui retourne une valeur candidate
 * @param {number} maxAttempts - Limite de tentatives (évite boucle infinie)
 * @returns {Array}
 */
export const generateDistractors = (correct, n, generateFn, maxAttempts = 100) => {
    const correctStr = String(correct);
    const distractors = new Set();
    let attempts = 0;
    while (distractors.size < n && attempts < maxAttempts) {
        const val = String(generateFn());
        if (val !== correctStr && !distractors.has(val)) {
            distractors.add(val);
        }
        attempts++;
    }
    return [...distractors];
};

/**
 * Filtre des candidats distracteurs pour un texte à trous : retire tout
 * candidat équivalent à un mot attendu ({{…}} de la phrase) — sinon le
 * distracteur devient une DEUXIÈME BONNE RÉPONSE au scoring (qui normalise
 * casse/accents/virgule). Retire aussi les doublons et les vides.
 *
 * Le filtre est volontairement PLUS agressif que le scoring (égalité numérique
 * incluse : "3,50" ≡ "3,5") : écarter un distracteur valide est sans gravité,
 * en garder un qui collisionne casse la question.
 *
 * @param {string} phrase - Phrase à trous ("… {{mot}} …")
 * @param {Array} candidats - Distracteurs candidats
 * @returns {Array<string>} Candidats sûrs, dédupliqués, dans l'ordre
 */
export const filtrerDistracteurs = (phrase, candidats) => {
    const normalise = (s) => String(s)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/,/g, '.')
        .trim();
    const estNombre = (s) => /^[+-]?\d+(\.\d+)?$/.test(s);

    const mots = [];
    // [^{}] et non [^}] : dans un trou de fraction \dfrac{7}{{{100}}} (triple
    // accolade, cf. fracTrouTex/preprocessBraces), [^}] capturerait « {100 »
    // et le candidat « 100 » passerait le filtre (vécu gen_1006, 2026-08-20).
    const re = /\{\{([^{}]+)\}\}/g;
    let m;
    while ((m = re.exec(String(phrase))) !== null) mots.push(normalise(m[1]));
    const motsSet = new Set(mots);
    const motsNum = new Set(mots.filter(estNombre).map(parseFloat));

    const vus = new Set();
    return candidats
        .map(c => String(c))
        .filter(c => {
            const n = normalise(c);
            if (!n || motsSet.has(n) || vus.has(n)) return false;
            if (estNombre(n) && motsNum.has(parseFloat(n))) return false;
            vus.add(n);
            return true;
        });
};

/**
 * Noms des rangs en français (index = rang positif).
 * rang 0 = unités, rang 1 = dizaines, ... rang 9 = milliards
 */
export const RANGS = [
    'unités', 'dizaines', 'centaines',
    'milliers', 'dizaines de milliers', 'centaines de milliers',
    'millions', 'dizaines de millions', 'centaines de millions',
    'milliards',
];

/**
 * Noms des classes de numération.
 */
export const CLASSES_NUMERATION = [
    'unités simples', 'milliers', 'millions', 'milliards',
];

/**
 * Convertit un label contenant des caractères subscripts Unicode (U+2080-U+2089)
 * en markup SVG compatible `<text>` : le bloc d'indices est rendu via un `<tspan>`
 * décalé (font-size réduit + dy positif), suivi d'un `<tspan dy>` inverse pour
 * restaurer la baseline. Utile pour les labels type "(d₁)", "(d₂)"... dans les
 * figures SVG où certaines fonts système ne supportent pas les glyphes subscripts.
 *
 * @param {string} name - Ex: "(d₁)", "(d₂)", "(d)" (sans indice → inchangé)
 * @returns {string} Markup SVG (innerText de <text>)
 */
export const subscriptToSvgTspan = (name) => {
    if (!name) return '';
    const SUB_CHARS = '₀₁₂₃₄₅₆₇₈₉';
    const m = name.match(/^([^\u2080-\u2089]*)([\u2080-\u2089]+)(.*)$/u);
    if (!m) return name;
    const [, prefix, subs, suffix] = m;
    const digits = [...subs].map(c => SUB_CHARS.indexOf(c)).join('');
    const tspanSub = `<tspan font-size="9" dy="3">${digits}</tspan>`;
    const tspanPost = suffix ? `<tspan dy="-3">${suffix}</tspan>` : '';
    return `${prefix}${tspanSub}${tspanPost}`;
};

// ─────────────────────────────────────────────────
// 1bis. TYPOGRAPHIE MATHÉMATIQUE (KaTeX)
// ─────────────────────────────────────────────────

/**
 * Emballe un fragment LaTeX dans les délimiteurs `$…$` attendus par l'app.
 *
 * ⚠️ POURQUOI LE PRÉFIXE `\mathrm{}` — piège réel, ne pas « simplifier » :
 * l'énoncé (`payload.texte`) est rendu par `RichTextBlockEleve.jsx` /
 * `HtmlKatex.jsx` avec une regex STRICTE `/\$(\\[^$]+)\$/g` — le fragment DOIT
 * commencer par une commande LaTeX, sinon `$x^2 = 49$` s'affiche brut à
 * l'écran, dollars compris. À l'inverse, `MathText.jsx` (étiquettes, QCM,
 * relier…) et `Print/helpers/renderKatex.js` (PDF) utilisent une regex
 * PERMISSIVE : sans ce garde-fou, un même énoncé s'imprimerait correctement et
 * s'afficherait cassé en ligne.
 *
 * `\mathrm{}` est un no-op de largeur nulle : le rendu de `x^2 = 49` et de
 * `\mathrm{}x^2 = 49` est identique. On ne touche donc pas aux renderers.
 *
 * @param {string} latex - fragment LaTeX SANS les `$` (ex: `2^{-2}`)
 * @returns {string} `$…$` prêt à être inséré dans un énoncé ou une étiquette
 */
export const tex = (latex) => {
    const s = String(latex);
    // `startsWith` et pas `includes` : la regex exige la commande en PREMIÈRE
    // position. `2 \times 2` contient bien un antislash mais commence par « 2 »
    // → il s'affichait « $2 \times 2$ » en clair (bug réel, vu à l'audit visuel).
    return `$${s.startsWith('\\') ? '' : '\\mathrm{}'}${s}$`;
};

/**
 * Fraction en KaTeX : `fracTex(17, 10)` → `$\dfrac{17}{10}$`.
 * Le signe négatif du numérateur est sorti devant la barre (usage français).
 * Dénominateur 1 → l'entier seul, sans fraction.
 *
 * ⚠️ Réservé à l'AFFICHAGE. Les réponses (bonne_reponse, mots de trous,
 * distracteurs) restent en texte brut : l'élève tape au clavier.
 */
export const fracTex = (num, den) => {
    if (Number(den) === 1) return tex(String(num));
    const signe = (Number(num) < 0) !== (Number(den) < 0) ? '-' : '';
    return tex(`${signe}\\dfrac{${Math.abs(Number(num))}}{${Math.abs(Number(den))}}`);
};

/** Racine carrée en KaTeX : `racineTex(78)` → `$\sqrt{78}$`. */
export const racineTex = (contenu) => tex(`\\sqrt{${contenu}}`);

/** Puissance en KaTeX : `puissTex(2, -2)` → `$2^{-2}$`. */
export const puissTex = (base, exposant) => tex(`${base}^{${exposant}}`);

/**
 * Fraction KaTeX d'une phrase à trous — le numérateur et/ou le dénominateur
 * peuvent être un trou `{{X}}`.
 *
 * L'accolade du groupe `\dfrac{…}` accolée aux `{{ }}` du trou produit la
 * convention **triple-accolade** `{{{X}}}`, décodée par `preprocessBraces`
 * (`components/VueQuestion/texteTrouMath.js`) avant l'extraction des trous par
 * `extractTrouWords`. Écrire la fraction à la main marche donc aussi — ce
 * helper existe pour que l'invariant soit nommé, testé
 * (`helpers-katex.self.test.js`) et impossible à casser par mégarde.
 *
 * `fracTrouTex('{{4}}', 19)` → `\dfrac{{{4}}}{19}` — SANS les `$` : à insérer
 * dans une ligne de phrase entourée de `$…$` (rendue en display mode centré).
 */
export const fracTrouTex = (num, den) => `\\dfrac{${num}}{${den}}`;

// ─────────────────────────────────────────────────
// 2. HELPERS DE PAYLOAD (format BanqueQuestion)
// ─────────────────────────────────────────────────

const LETTRES = 'ABCDEFGH';

/**
 * Construit le payload de base au format BanqueQuestion.
 * @param {string} type - 'qcm' | 'texte' | 'association'
 * @param {string} texte - Énoncé HTML
 * @param {Object} contenu - Contenu spécifique au type
 * @param {Object} options - { niveau, matiere, attendu, label }
 * @param {Object|null} elementsAdditionnels - Éléments additionnels (tableau, image, etc.)
 * @returns {Object} Payload complet
 */
export const basePayload = (type, texte, contenu, options = {}, elementsAdditionnels = null) => ({
    type,
    texte,
    contenu,
    elements_additionnels: elementsAdditionnels || options.elements_additionnels || options.elements || null,
    niveau: options.niveau || '6eme',
    matiere: options.matiere || 'maths',
    attendu: options.attendu || null,
    _meta: { label: options.label || '' },
});

/**
 * Construit un payload QCM (simple ou multiple).
 * Les choix sont automatiquement mélangés.
 *
 * @param {Array} choixList - Tous les choix (correct inclus)
 * @param {*|Array} correct - La bonne réponse (string) ou les bonnes réponses (array) si multi
 * @param {string} texte - Énoncé HTML
 * @param {Object} options - { niveau, matiere, attendu, label }
 * @param {boolean} multi - true = QCM multiple
 * @returns {Object} Payload QCM
 */
export const makeQcm = (choixList, correct, texte, options = {}, multi = false) => {
    // INVARIANT (revue 2026-08-18) : jamais deux options STRICTEMENT identiques.
    // Comparaison TEXTUELLE, lettre à lettre — jamais mathématique : « 4,0 » et
    // « 4,00 » sont deux options légitimement distinctes (ex. « quel nombre a
    // 2 décimales ? ») et la pertinence sémantique des distracteurs reste la
    // responsabilité de chaque gen. Mais deux textes identiques à l'écran sont
    // indiscernables pour l'élève, et la clé pointant la PREMIÈRE occurrence
    // (indexOf), cocher la jumelle serait compté faux : on garantit l'unicité
    // ICI, à la construction, pour tous les gens. Première occurrence gardée
    // (la bonne réponse survit toujours) ; le harnais reste le filet
    // (S-QCM-DUP-BRUT, S-QCM-MIN3).
    const vus = new Set();
    const uniques = (choixList || []).filter((ch) => {
        const cle = typeof ch === 'string' ? ch : JSON.stringify(ch);
        if (vus.has(cle)) return false;
        vus.add(cle);
        return true;
    });
    const choix = shuffle(uniques);
    if (multi) {
        // Multi : correct est un array de bonnes réponses. Lettres dédoublonnées
        // aussi : une valeur en double y produirait une clé ['A','A'], impossible
        // à cocher (on ne coche une case qu'une fois) → toujours compté faux.
        const br = [...new Set(correct.map(x => LETTRES[choix.indexOf(x)]))].sort();
        return basePayload('qcm', texte, { choix, bonne_reponse: br, multi: true }, options);
    }
    // Simple : correct est une seule valeur
    const br = LETTRES[choix.indexOf(correct)];
    return basePayload('qcm', texte, { choix, bonne_reponse: br }, options);
};

/**
 * Construit un payload saisie (texte libre).
 *
 * @param {string} bonneReponse - La réponse attendue
 * @param {string} texte - Énoncé HTML
 * @param {Object} options - { niveau, matiere, attendu, label, strict, suffixe }
 * @returns {Object} Payload saisie
 */
export const makeSaisie = (bonneReponse, texte, options = {}) =>
    basePayload('texte', texte, {
        bonne_reponse: String(bonneReponse),
        strict: options.strict || false,
        suffixe: options.suffixe || '',
        // Comparaison d'expression (2026-09-13) : le flag et la liste des écritures
        // admises voyagent dans contenu, comme strict (cf. questionUtils.compareAnswers).
        ...(options.comparaison ? { comparaison: options.comparaison } : {}),
        ...(options.reponses_acceptees ? { reponses_acceptees: options.reponses_acceptees } : {}),
    }, options);

/**
 * Les écritures admises d'un POLYNÔME donné par ses termes ({ coef, deg }),
 * pour une saisie en comparaison d'expression (contenu.comparaison =
 * 'expression', décision prof 2026-09-13) : tous les ordres des termes, le
 * « + » de tête facultatif, et « 2x » comme « 2*x ». La première écriture
 * (les termes dans l'ordre donné) est la forme canonique, à passer en
 * bonne_reponse. Le carré s'écrit « x² » (la touche ² existe sur les claviers,
 * décision prof 2026-09-13) ; « x^2 » tapé est admis, le juge ramène ² à ^2.
 * Les espaces n'ont pas d'importance (le juge les retire).
 *
 * @param {Array<{coef: number, deg: number}>} termes - coefficients entiers non nuls, degré 0..2
 * @param {string} lettre - la lettre
 * @returns {string[]} écritures, la canonique en premier, sans doublon
 */
export const ecrituresPolynome = (termes, lettre = 'x') => {
    const mono = (t, etoile) => {
        const a = Math.abs(t.coef);
        const puissance = t.deg === 0 ? '' : t.deg === 1 ? lettre : t.deg === 2 ? `${lettre}\u00b2` : `${lettre}^${t.deg}`;
        if (t.deg === 0) return String(a);
        return a === 1 ? puissance : `${a}${etoile ? '*' : ''}${puissance}`;
    };
    const ecrire = (ordre, plusDeTete, etoile) => ordre.map((t, i) => {
        const signe = t.coef < 0 ? '-' : (i === 0 ? (plusDeTete ? '+' : '') : '+');
        return `${i === 0 ? '' : ' '}${signe}${i === 0 ? '' : ' '}${mono(t, etoile)}`;
    }).join('');
    const permutations = (arr) => (arr.length <= 1 ? [arr] : arr.flatMap((t, i) => permutations([...arr.slice(0, i), ...arr.slice(i + 1)]).map((p) => [t, ...p])));
    const out = [];
    for (const ordre of permutations(termes)) {
        for (const etoile of [false, true]) {
            for (const plus of [false, true]) {
                if (plus && ordre[0].coef < 0) continue;
                if (etoile && !ordre.some((t) => t.deg > 0 && Math.abs(t.coef) !== 1)) continue;
                const e = ecrire(ordre, plus, etoile);
                if (!out.includes(e)) out.push(e);
            }
        }
    }
    return out;
};

/**
 * Construit un payload tableau Vrai/Faux.
 * Techniquement un QCM avec elements_additionnels de type table.
 *
 * @param {Array<string>} lignes - Les affirmations
 * @param {Array<number>} bonnesReponses - Index de la colonne correcte par ligne (0=Vrai, 1=Faux)
 * @param {string} texte - Énoncé HTML
 * @param {Object} options - { niveau, matiere, attendu, label, colonnes }
 * @returns {Object} Payload tableau V/F
 */
export const makeVraiFaux = (lignes, bonnesReponses, texte, options = {}) => {
    // Dédupliquer les propositions identiques (garde la première occurrence)
    const seen = new Set();
    const uniqLignes = [];
    const uniqBonnes = [];
    lignes.forEach((ligne, i) => {
        if (!seen.has(ligne)) {
            seen.add(ligne);
            uniqLignes.push(ligne);
            uniqBonnes.push(bonnesReponses[i]);
        }
    });

    const colonnes = options.colonnes || ['Vrai', 'Faux'];
    // bonne_reponse au format [ligne|colonne]
    const br = uniqLignes.map((ligne, i) => `[${ligne}|${colonnes[uniqBonnes[i]]}]`).join('');

    const tableElement = {
        type: 'table',
        config: {
            type: 'vraiFaux',
            colonnes,
            lignes: uniqLignes,
            bonnesReponses: uniqBonnes,
        },
    };

    // Merge avec les éléments additionnels éventuellement fournis (droite graduée,
    // expressions KaTeX...). Le tableau est ajouté en dernier et sera filtré
    // par le renderer qui l'affiche séparément.
    const extraFromOptions = options.elements_additionnels?.elements || [];
    const merged = {
        version: 1,
        elements: [...extraFromOptions, tableElement],
    };

    return basePayload('qcm', texte, { choix: [], bonne_reponse: br }, options, merged);
};

/**
 * Construit un payload texte à trous.
 * La phrase contient des {{mots}} qui sont les trous.
 *
 * @param {string} phrase - Phrase avec {{trous}}. Ex: "Le {{résultat}} de 3×4 est {{12}}"
 * @param {string} texte - Énoncé HTML
 * @param {Object} options - { niveau, matiere, attendu, label, mode, distracteurs, strict }
 * @returns {Object} Payload texte à trous
 */
export const makeTexteTrou = (phrase, texte, options = {}) =>
    basePayload('association', texte, {
        subType: 'texte_trou',
        phrase,
        mode: options.mode || 'drag',
        distracteurs: options.distracteurs || [],
        strict: options.strict || false,
        // Groupes de trous SANS ORDRE, ex. [[0, 1]] : les trous 0 et 1 se remplissent
        // dans n'importe quel ordre. À ne poser que si l'élève qui les échange écrit
        // encore quelque chose de VRAI — une somme, un produit. Jamais sur une
        // soustraction, un quotient et son reste, ou des trous qui portent des rôles
        // différents. Voir trousInterchangeables dans utils/questionUtils.js.
        trousInterchangeables: options.trousInterchangeables || [],
        bonne_reponse: phrase,
    }, options);

/**
 * Construit un payload relier (paires source → target).
 *
 * @param {Array<{source: string, target: string}>} pairs - Les paires à relier
 * @param {string} texte - Énoncé HTML
 * @param {Object} options - { niveau, matiere, attendu, label,
 *   cibles_leurres?: Array<{target: string, code: string}> } — cibles
 *   surnuméraires (aucune source ne leur correspond) ; `code` nomme l'erreur
 *   d'élève matérialisée (feedback pédagogique). INVARIANT : bonne_reponse est
 *   construite UNIQUEMENT depuis pairs — les leurres n'y entrent jamais, le
 *   scoring et les solveurs y sont donc indifférents.
 * @returns {Object} Payload relier
 */
export const makeRelier = (pairs, texte, options = {}) => {
    // bonne_reponse = JSON array de [source, target] trié par source
    const sorted = [...pairs].sort((a, b) => a.source.localeCompare(b.source));
    const br = JSON.stringify(sorted.map(p => [p.source, p.target]));
    const leurres = options.cibles_leurres || [];
    return basePayload('association', texte, {
        subType: 'relier',
        pairs,
        // Champ émis SEULEMENT si non vide : les payloads sans leurres restent
        // strictement identiques (stabilité des empreintes du harnais).
        ...(leurres.length ? { cibles_leurres: leurres } : {}),
        bonne_reponse: br,
    }, options);
};

/**
 * Construit un payload ordonner.
 *
 * @param {Array<string>} items - Les éléments dans l'ordre correct
 * @param {string} texte - Énoncé HTML
 * @param {Object} options - { niveau, matiere, attendu, label }
 * @returns {Object} Payload ordonner
 */
export const makeOrdonner = (items, texte, options = {}) =>
    basePayload('association', texte, {
        subType: 'ordonner',
        items,
        bonne_reponse: JSON.stringify(items),
    }, options);

/**
 * Construit un payload classer (catégoriser).
 *
 * @param {Array<{name: string, items: Array<string>}>} categories - Les catégories avec leurs items
 * @param {string} texte - Énoncé HTML
 * @param {Object} options - { niveau, matiere, attendu, label }
 * @returns {Object} Payload classer
 */
export const makeClasser = (categories, texte, options = {}) => {
    // bonne_reponse = JSON object { catégorie: [items triés] } avec clés triées
    const sorted = {};
    [...categories].sort((a, b) => a.name.localeCompare(b.name)).forEach(cat => {
        sorted[cat.name] = [...cat.items].sort();
    });
    return basePayload('association', texte, {
        subType: 'classer',
        categories,
        bonne_reponse: JSON.stringify(sorted),
    }, options);
};

/**
 * Construit un payload "programme_construction" (tracé interactif de cercles sur SVG).
 * L'élève suit une liste de consignes, clique un centre (snap sur points nommés),
 * puis drag pour définir le rayon. Feedback immédiat par tâche.
 *
 * @param {Object} spec
 *   - pxPerCm: number (ex: 30)
 *   - tolerance: number (px, défaut 12)
 *   - viewBox: {w, h}
 *   - points: Array<{name, x, y}> — points nommés visibles
 *   - preShapes: Array<{type, from, to}> — segments/formes pré-tracés
 *   - tasks: Array<{id, consigne, color, shape: {type:'circle', cx, cy, r}}>
 * @param {string} texte - Énoncé HTML
 * @param {Object} options - { niveau, matiere, attendu, label }
 * @returns {Object} Payload programme_construction
 */
export const makeProgrammeConstruction = (spec, texte, options = {}) => {
    // bonne_reponse = { taskId: {cx, cy, r} } pour un cercle, { type: 'line', p1, p2 } pour une droite.
    // Les cercles ne portent PAS de champ `type` (compat historique : absence de type → cercle).
    const bonneReponse = {};
    spec.tasks.forEach(t => {
        if (t.shape.type === 'line' || t.shape.type === 'segment') {
            bonneReponse[t.id] = { type: t.shape.type, p1: t.shape.p1, p2: t.shape.p2 };
        } else if (t.shape.type === 'ray') {
            bonneReponse[t.id] = {
                type: 'ray',
                from: t.shape.from,
                angleDeg: t.shape.angleDeg,
                ...(t.shape.labelEnd ? { labelEnd: t.shape.labelEnd } : {}),
            };
        } else {
            bonneReponse[t.id] = { cx: t.shape.cx, cy: t.shape.cy, r: t.shape.r };
        }
    });
    // Côté élève : les tasks n'incluent PAS le shape attendu (séparation consigne / solution)
    const tasksForStudent = spec.tasks.map(({ shape, ...rest }) => rest);
    return basePayload('association', texte, {
        subType: 'programme_construction',
        pxPerCm: spec.pxPerCm,
        tolerance: spec.tolerance ?? 12,
        viewBox: spec.viewBox,
        points: spec.points,
        preShapes: spec.preShapes || [],
        tasks: tasksForStudent,
        // Unité de la FIGURE (bilan prof D6, 2026-07-27) : l'unité de travail
        // est le carreau de la grille, jamais le centimètre physique (au print
        // la figure est redimensionnée par le CSS). 'cm' par défaut ; spread
        // conditionnel → payloads existants inchangés bit à bit.
        ...(spec.unite && spec.unite !== 'cm' ? { unite: spec.unite } : {}),
        bonne_reponse: JSON.stringify(bonneReponse),
    }, options);
};

/**
 * Construit un payload « placer les aiguilles » (horloge interactive, gen_1032).
 * L'élève fait tourner les aiguilles d'un cadran pour indiquer l'heure donnée
 * dans l'énoncé (composant HorlogeAiguillesEleve).
 *
 * @param {Object} heure - { h (1..11), min (multiple de 5), sec (multiple de 5) | null }
 * @param {string} texte - Énoncé HTML (porte l'heure à afficher)
 * @param {Object} options - { niveau, matiere, attendu, label }
 * @returns {Object} Payload placer_aiguilles
 *
 * bonne_reponse = JSON { aiguilles: { h, min, sec } } — SÉMANTIQUE (pas des
 * angles) ; la clé `aiguilles` est le discriminant du scoring (cf.
 * isAiguillesJson dans src/utils/questionUtils.js). La réponse élève est en
 * angles posés { heures, minutes, secondes? } (degrés depuis 12 h, sens
 * horaire) ; les règles de validation (le bon repère de 5 min ; la petite
 * strictement DANS l'intervalle, pile sur le chiffre seulement à min = 0)
 * vivent dans questionUtils.aiguilleJuste.
 */
/**
 * Retire des codes d'erreur d'une question (relecture à l'aveugle du 03/10/2026,
 * docs/kit/REGLES_EQUIPE.md) : le choix reste proposé à l'élève, il part sans nom.
 * Rien d'autre ne change, ni les tirages ni les choix. `codes` : les codes à
 * retirer pour ce format (undefined : rien à faire).
 */
export const retirerCodesErreur = (payload, codes) => {
    if (!codes || !codes.length || !Array.isArray(payload?.contenu?.erreurs)) return payload;
    const erreurs = payload.contenu.erreurs.filter((e) => !codes.includes(e?.code));
    const contenu = { ...payload.contenu };
    if (erreurs.length) contenu.erreurs = erreurs; else delete contenu.erreurs;
    payload.contenu = contenu;
    return payload;
};

export const makePlacerAiguilles = (heure, texte, options = {}) => {
    if (!(heure.h >= 1 && heure.h <= 11)) throw new Error(`makePlacerAiguilles : h hors [1;11] (${heure.h})`);
    if (heure.min % 5 !== 0 || heure.min < 0 || heure.min > 55) throw new Error(`makePlacerAiguilles : min invalide (${heure.min})`);
    if (heure.sec != null && (heure.sec % 5 !== 0 || heure.sec < 0 || heure.sec > 55)) throw new Error(`makePlacerAiguilles : sec invalide (${heure.sec})`);
    return basePayload('association', texte, {
        subType: 'placer_aiguilles',
        avecTrotteuse: heure.sec != null,
        bonne_reponse: JSON.stringify({ aiguilles: { h: heure.h, min: heure.min, sec: heure.sec ?? null } }),
    }, options);
};
